import { useState, useRef } from "react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { OPTION_COLORS, COLOR_LIST, OptionBadge } from "@/components/badges";
import { Plus, Trash2, Eye, EyeOff, Settings2, X, Pencil, Check } from "lucide-react";

const TYPES = [
  { value: "text", label: "Metin" },
  { value: "number", label: "Sayı" },
  { value: "date", label: "Tarih" },
  { value: "textarea", label: "Uzun Metin" },
  { value: "select", label: "Seçenek Listesi" },
];

const TR_MAP = { ç: "c", ğ: "g", ı: "i", İ: "i", ö: "o", ş: "s", ü: "u" };
const slugify = (name) =>
  name
    .trim()
    .split("")
    .map((ch) => TR_MAP[ch] || ch)
    .join("")
    .toLowerCase()
    .replace(/\s+/g, "_")
    .replace(/[^a-z0-9_]/g, "");

export default function ColumnManager({ open, onOpenChange, columns, onSave }) {
  const [editingOptions, setEditingOptions] = useState(null);
  const [renaming, setRenaming] = useState(null); // {key, value}
  const [newColName, setNewColName] = useState("");
  const [newColType, setNewColType] = useState("text");
  const [newOptVal, setNewOptVal] = useState("");
  const [newOptColor, setNewOptColor] = useState("blue");

  const update = (next) => onSave(next);

  const uniqueKey = (name) => {
    const base = slugify(name) || "sutun";
    const keys = new Set(columns.map((c) => c.key));
    let key = base;
    let i = 1;
    while (keys.has(key)) key = `${base}_${i++}`;
    return key;
  };

  const toggleVisible = (key) =>
    update(columns.map((c) => (c.key === key ? { ...c, visible: !c.visible } : c)));

  const removeCol = (key) => update(columns.filter((c) => c.key !== key));

  const saveRename = () => {
    if (!renaming || !renaming.value.trim()) {
      setRenaming(null);
      return;
    }
    update(columns.map((c) => (c.key === renaming.key ? { ...c, label: renaming.value.trim() } : c)));
    setRenaming(null);
  };

  const addColumn = () => {
    if (!newColName.trim()) return;
    const key = uniqueKey(newColName);
    update([
      ...columns,
      { key, label: newColName.trim(), type: newColType, width: 160, visible: true, options: [] },
    ]);
    setNewColName("");
    setNewColType("text");
  };

  const addOption = (key) => {
    if (!newOptVal.trim()) return;
    update(
      columns.map((c) =>
        c.key === key
          ? { ...c, options: [...(c.options || []), { value: newOptVal.trim(), color: newOptColor }] }
          : c
      )
    );
    setNewOptVal("");
  };

  const removeOption = (key, val) =>
    update(
      columns.map((c) =>
        c.key === key ? { ...c, options: (c.options || []).filter((o) => o.value !== val) } : c
      )
    );

  const changeType = (key, type) =>
    update(columns.map((c) => (c.key === key ? { ...c, type } : c)));

  const optDrag = useRef(null);
  const reorderOptions = (key, from, to) => {
    if (from == null || from === to) return;
    update(
      columns.map((c) => {
        if (c.key !== key) return c;
        const opts = [...(c.options || [])];
        const [m] = opts.splice(from, 1);
        opts.splice(to, 0, m);
        return { ...c, options: opts };
      })
    );
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-md overflow-y-auto grid-scroll">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <Settings2 className="w-5 h-5" /> Sütun Yönetimi
          </SheetTitle>
          <SheetDescription>
            Sütun adını değiştirin, gizle/göster, yeni sütun ekle ve seçenekleri düzenle.
          </SheetDescription>
        </SheetHeader>

        {/* Add column */}
        <div className="mt-5 p-3 rounded-lg border border-border bg-secondary/40 space-y-2">
          <Label className="text-xs">Yeni Sütun Ekle</Label>
          <div className="flex gap-2">
            <Input
              value={newColName}
              onChange={(e) => setNewColName(e.target.value)}
              placeholder="Sütun adı"
              onKeyDown={(e) => e.key === "Enter" && addColumn()}
              data-testid="new-column-name"
            />
            <Select value={newColType} onValueChange={setNewColType}>
              <SelectTrigger className="w-36" data-testid="new-column-type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {TYPES.map((t) => (
                  <SelectItem key={t.value} value={t.value}>
                    {t.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button size="sm" className="w-full" onClick={addColumn} data-testid="add-column-button">
            <Plus className="w-4 h-4 mr-1" /> Ekle
          </Button>
        </div>

        {/* Column list */}
        <div className="mt-4 space-y-2">
          {columns.map((col) => (
            <div key={col.key} className="rounded-lg border border-border p-2.5">
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0 flex-1">
                  <button
                    onClick={() => toggleVisible(col.key)}
                    className="text-muted-foreground hover:text-foreground flex-shrink-0"
                    data-testid={`toggle-visible-${col.key}`}
                  >
                    {col.visible ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4 opacity-50" />}
                  </button>
                  {renaming && renaming.key === col.key ? (
                    <div className="flex items-center gap-1 flex-1">
                      <Input
                        autoFocus
                        value={renaming.value}
                        onChange={(e) => setRenaming({ key: col.key, value: e.target.value })}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") saveRename();
                          if (e.key === "Escape") setRenaming(null);
                        }}
                        className="h-7 text-sm"
                        data-testid={`rename-input-${col.key}`}
                      />
                      <Button size="icon" className="h-7 w-7" onClick={saveRename} data-testid={`rename-save-${col.key}`}>
                        <Check className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  ) : (
                    <>
                      <span className={`text-sm font-medium truncate ${!col.visible && "opacity-50"}`}>
                        {col.label}
                      </span>
                      <Select value={col.type} onValueChange={(t) => changeType(col.key, t)}>
                        <SelectTrigger
                          className="h-6 w-[96px] text-[10px] px-1.5 flex-shrink-0"
                          data-testid={`col-type-${col.key}`}
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {TYPES.map((t) => (
                            <SelectItem key={t.value} value={t.value} className="text-xs">
                              {t.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </>
                  )}
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  {(!renaming || renaming.key !== col.key) && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-muted-foreground hover:text-primary"
                      onClick={() => setRenaming({ key: col.key, value: col.label })}
                      data-testid={`rename-column-${col.key}`}
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </Button>
                  )}
                  {col.type === "select" && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 text-xs"
                      onClick={() => setEditingOptions(editingOptions === col.key ? null : col.key)}
                      data-testid={`edit-options-${col.key}`}
                    >
                      Seçenekler
                    </Button>
                  )}
                  {col.key !== "id" && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-muted-foreground hover:text-destructive"
                      onClick={() => removeCol(col.key)}
                      data-testid={`remove-column-${col.key}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  )}
                </div>
              </div>

              {col.type === "select" && editingOptions === col.key && (
                <div className="mt-3 pt-3 border-t border-border space-y-2">
                  <div className="flex flex-wrap gap-1.5">
                    {(col.options || []).map((o, oi) => (
                      <span
                        key={o.value}
                        draggable
                        onDragStart={() => (optDrag.current = oi)}
                        onDragOver={(e) => e.preventDefault()}
                        onDrop={() => reorderOptions(col.key, optDrag.current, oi)}
                        className="inline-flex items-center gap-1 cursor-grab active:cursor-grabbing"
                        data-testid={`option-${col.key}-${o.value}`}
                      >
                        <OptionBadge value={o.value} color={o.color} />
                        <button
                          onClick={() => removeOption(col.key, o.value)}
                          className="text-muted-foreground hover:text-destructive"
                          data-testid={`remove-option-${col.key}-${o.value}`}
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                    {(col.options || []).length === 0 && (
                      <span className="text-xs text-muted-foreground">Seçenek yok</span>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Input
                      value={editingOptions === col.key ? newOptVal : ""}
                      onChange={(e) => setNewOptVal(e.target.value)}
                      placeholder="Yeni seçenek"
                      className="h-8 text-sm"
                      onKeyDown={(e) => e.key === "Enter" && addOption(col.key)}
                      data-testid={`new-option-value-${col.key}`}
                    />
                    <Select value={newOptColor} onValueChange={setNewOptColor}>
                      <SelectTrigger className="w-24 h-8">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {COLOR_LIST.map((c) => (
                          <SelectItem key={c} value={c}>
                            <span className="flex items-center gap-2">
                              <span className={`w-3 h-3 rounded-full ${OPTION_COLORS[c].split(" ")[0]}`} />
                              {c}
                            </span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => addOption(col.key)}
                      data-testid={`add-option-${col.key}`}
                    >
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </SheetContent>
    </Sheet>
  );
}
